import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';

const GameArea3D = ({ carPosition, gameState, onTap, progress }) => {
  const [hasFirstTap, setHasFirstTap] = useState(false);
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const carRef = useRef(null);
  const roadRef = useRef(null);
  const animationIdRef = useRef(null);

  // Initialize Three.js scene
  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x87CEEB);
    sceneRef.current = scene;

    // Camera setup - extend far plane to see entire game distance
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 2000);
    camera.position.set(0, 2, -5);
    cameraRef.current = camera;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.setClearColor(0x87CEEB, 1); // Set clear color explicitly
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(10, 10, 5);
    directionalLight.castShadow = true;
    directionalLight.shadow.camera.far = 2000; // Extend shadow camera far plane
    scene.add(directionalLight);

    // Ground - extend to cover entire game distance
    const groundGeometry = new THREE.PlaneGeometry(2000, 3000);
    const groundMaterial = new THREE.MeshStandardMaterial({ color: 0x90EE90 });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.2;
    ground.receiveShadow = true;
    scene.add(ground);

    // Sky
    const skyGeometry = new THREE.SphereGeometry(2000, 32, 32);
    const skyMaterial = new THREE.MeshBasicMaterial({ 
      color: 0x87CEEB, 
      side: THREE.BackSide 
    });
    const sky = new THREE.Mesh(skyGeometry, skyMaterial);
    scene.add(sky);

    // Road - extend to cover entire game distance with better material
    const roadGeometry = new THREE.PlaneGeometry(20, 3000);
    const roadMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x444444,
      transparent: false, // Disable transparency to prevent z-fighting
      opacity: 1.0,
      depthWrite: true,
      depthTest: true
    });
    const road = new THREE.Mesh(roadGeometry, roadMaterial);
    road.rotation.x = -Math.PI / 2;
    road.position.y = -0.1;
    road.receiveShadow = true; // Enable shadow receiving
    roadRef.current = road;
    scene.add(road);

    // Road lines - create continuous dashed line effect for entire distance
    for (let i = 0; i < 600; i++) { // Cover entire 3000 unit distance
      const lineGeometry = new THREE.BoxGeometry(0.2, 0.02, 3); // Shorter lines for dashed effect
      const lineMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff });
      const line = new THREE.Mesh(lineGeometry, lineMaterial);
      
      // Create dashed line effect by skipping some positions
      if (i % 2 === 0) { // Only place lines at even positions for dashed effect
        line.position.set(0, 0.01, i * 5); // Closer spacing (5 units instead of 10)
        scene.add(line);
      }
    }

    // Trees - positioned in two rows on either side of the road
    for (let i = 0; i < 50; i++) {
      // Left side trees (negative x)
      const trunkLeft = new THREE.CylinderGeometry(0.5, 0.5, 4, 8);
      const trunkMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
      const trunkL = new THREE.Mesh(trunkLeft, trunkMaterial);
      const leftX = -15 - Math.random() * 10; // Between -15 and -25 units from center
      const leftZ = i * 40 + Math.random() * 20; // Add some randomness to z position
      trunkL.position.set(leftX, 2, leftZ);
      trunkL.castShadow = true;
      scene.add(trunkL);

      const leavesLeft = new THREE.SphereGeometry(2, 8, 8);
      const leavesMaterial = new THREE.MeshStandardMaterial({ color: 0x228B22 });
      const leavesL = new THREE.Mesh(leavesLeft, leavesMaterial);
      leavesL.position.set(leftX, 6, leftZ); // Position leaves directly above trunk
      leavesL.castShadow = true;
      scene.add(leavesL);

      // Right side trees (positive x)
      const trunkRight = new THREE.CylinderGeometry(0.5, 0.5, 4, 8);
      const trunkR = new THREE.Mesh(trunkRight, trunkMaterial);
      const rightX = 15 + Math.random() * 10; // Between +15 and +25 units from center
      const rightZ = i * 40 + Math.random() * 20;
      trunkR.position.set(rightX, 2, rightZ);
      trunkR.castShadow = true;
      scene.add(trunkR);

      const leavesRight = new THREE.SphereGeometry(2, 8, 8);
      const leavesR = new THREE.Mesh(leavesRight, leavesMaterial);
      leavesR.position.set(rightX, 6, rightZ); // Position leaves directly above trunk
      leavesR.castShadow = true;
      scene.add(leavesR);
    }

    // Car
    const carGroup = new THREE.Group();
    
    // Car body
    const bodyGeometry = new THREE.BoxGeometry(2, 1, 3);
    const bodyMaterial = new THREE.MeshStandardMaterial({ color: 0xff4444 });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.castShadow = true;
    carGroup.add(body);

    // Car top
    const topGeometry = new THREE.BoxGeometry(1.5, 0.5, 1.5);
    const topMaterial = new THREE.MeshStandardMaterial({ color: 0xcc3333 });
    const top = new THREE.Mesh(topGeometry, topMaterial);
    top.position.y = 0.75;
    top.position.z = -0.5;
    top.castShadow = true;
    carGroup.add(top);

    // Wheels
    const wheelGeometry = new THREE.CylinderGeometry(0.4, 0.4, 0.2, 16);
    const wheelMaterial = new THREE.MeshStandardMaterial({ color: 0x333333 });
    
    const wheel1 = new THREE.Mesh(wheelGeometry, wheelMaterial);
    wheel1.rotation.z = Math.PI / 2; // rotate 90 degrees
    wheel1.position.set(-1, -0.25, 1);
    wheel1.castShadow = true;
    carGroup.add(wheel1);

    const wheel2 = new THREE.Mesh(wheelGeometry, wheelMaterial);
    wheel2.rotation.z = Math.PI / 2; // rotate 90 degrees

    wheel2.position.set(1, -0.25, 1);
    wheel2.castShadow = true;
    carGroup.add(wheel2);

    const wheel3 = new THREE.Mesh(wheelGeometry, wheelMaterial);
    wheel3.rotation.z = Math.PI / 2; // rotate 90 degrees

    wheel3.position.set(-1, -0.25, -1);
    wheel3.castShadow = true;
    carGroup.add(wheel3);

    const wheel4 = new THREE.Mesh(wheelGeometry, wheelMaterial);
    wheel4.rotation.z = Math.PI / 2; // rotate 90 degrees

    wheel4.position.set(1, -0.25, -1);
    wheel4.castShadow = true;
    carGroup.add(wheel4);

    // Headlights
    const lightGeometry = new THREE.SphereGeometry(0.2, 16, 16);
    const lightMaterial = new THREE.MeshBasicMaterial({ 
      color: 0xffffaa,
      emissive: 0xffffaa,
      emissiveIntensity: 0.5
    });
    const headlight = new THREE.Mesh(lightGeometry, lightMaterial);
    headlight.position.set(0, 0.5, 1.6);
    carGroup.add(headlight);

    carGroup.position.set(0, 0.5, carPosition);
    carRef.current = carGroup;
    scene.add(carGroup);

    // Finish line
    const finishGroup = new THREE.Group();
    for (let i = 0; i < 20; i++) {
      const checkGeometry = new THREE.BoxGeometry(0.5, 0.04, 0.5);
      const checkMaterial = new THREE.MeshStandardMaterial({ 
        color: i % 2 === 0 ? 0xffffff : 0x000000 
      });
      const check = new THREE.Mesh(checkGeometry, checkMaterial);
      check.position.set(i * 0.5 - 5, 0.02, 1000);
      console.log(`🚗 Finish line : ${check.position.x} , ${check.position.y}, ${check.position.z} (${progress.toFixed(1)}% progress)`);

      finishGroup.add(check);
    }
    scene.add(finishGroup);

    // Handle window resize
    const handleResize = () => {
      if (mountRef.current && renderer) {
        const width = mountRef.current.clientWidth;
        const height = mountRef.current.clientHeight;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
      }
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  // Animation loop - separate from scene setup
  useEffect(() => {
    if (!sceneRef.current || !rendererRef.current || !cameraRef.current) return;

    // Prevent multiple animation loops from running
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }

    // Use a ref to track if animation is already running
    const isRunningRef = { current: true };

    const animate = () => {
      // Only run if component is still mounted and animation should continue
      if (!isRunningRef.current || !sceneRef.current || !rendererRef.current || !cameraRef.current) {
        return;
      }

      animationIdRef.current = requestAnimationFrame(animate);
      
      // Update car position
      if (carRef.current) {
        carRef.current.position.z = carPosition;
      }

      // Update finish line position
      if (sceneRef.current) {
        // Find the finish line group
        const finishLine = sceneRef.current.children.find(child => 
          child.type === 'Group' && child.children.length === 20
        );
        if (finishLine) {
          finishLine.position.z = 1000 - carPosition;
        }
      }

      // Update camera to follow car
      const targetZ = carPosition - 5;
      cameraRef.current.position.z += (targetZ - cameraRef.current.position.z) * 0.1;
      cameraRef.current.lookAt(0, 2, carPosition + 10);

      // Add subtle car movement
      if (carRef.current) {
        carRef.current.rotation.y = Math.sin(Date.now() * 0.002) * 0.1;
      }

      rendererRef.current.render(sceneRef.current, cameraRef.current);
    };
    
    animate();

    return () => {
      isRunningRef.current = false;
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
    };
  }, [carPosition]); // Make animation loop reactive to carPosition changes

  const handleTap = (event) => {
    if (!hasFirstTap) {
      setHasFirstTap(true);
    }
    onTap(event);
  };

  const handleCanvasClick = (event) => {
    if (gameState === 'playing') {
      handleTap(event);
    }
  };

  return (
    <div className="game-area-3d">
      {/* Progress Bar - Only show after first tap */}
      {hasFirstTap && (
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="progress-text">{Math.round(progress)}% Complete</div>
        </div>
      )}

      {/* 3D Canvas */}
      <div 
        ref={mountRef} 
        style={{ 
          height: '70vh', 
          width: '100%',
          cursor: gameState === 'playing' ? 'pointer' : 'default'
        }}
        onClick={handleCanvasClick}
      />

      {/* Tap Instruction */}
      <div 
        className="tap-instruction"
        style={{
          animation: gameState === 'playing' ? 'pulse 0.5s infinite' : 'pulse 2s infinite',
          background: gameState === 'won' ? 'rgba(76, 175, 80, 0.8)' : 'rgba(0,0,0,0.7)',
          display: hasFirstTap && gameState !== 'won' ? 'none' : 'block'
        }}
      >
        {gameState === 'won' ? '🎉 YOU WIN! 🎉' : 
         gameState === 'paused' ? '⏸️ GAME PAUSED - Click or press Tab to continue!' :
         'CLICK IN THE 3D AREA OR PRESS TAB TO PUSH THE CAR!'}
      </div>
    </div>
  );
};

export default GameArea3D;
